import { getDrofusChangeReason } from "./drofusChangeReason";
import { createSelector } from "@reduxjs/toolkit";
import {
  openapi,
  type PostMultiplacerDrofusInterfaceProjectChangeEventsApiArg as IChangeEventsApiArg,
  type PostMultiplacerDrofusInterfaceProjectChangeEventAttributeOptionsApiArg as IAttributeOptionsApiArg,
  type PostMultiplacerDrofusInterfaceProjectChangeEventsByRoomApiArg as IRoomGroupsApiArg,
} from "../../openapi";
import { selectHttpMcbimSpacesResult } from "../McBim/httpMcBimSelectors";
import type { IRootState } from "../app/store";

export const selectDrofusEndringerState = (state: IRootState) => state.drofusEndringer;

export const selectDrofusEndringerIsFloorView = (state: IRootState) =>
  state.global.activeProjectTab !== "endringer";

export const selectDrofusEndringerShowPns = (state: IRootState) =>
  !selectDrofusEndringerIsFloorView(state);

const selectDrofusEndringerFloorRoomNumbers = createSelector(
  (state: IRootState) =>
    selectDrofusEndringerIsFloorView(state) ? selectHttpMcbimSpacesResult(state) : undefined,
  (spaces) => [
    ...new Set(
      (spaces ?? [])
        .map((space) => space.name?.trim())
        .filter((name): name is string => Boolean(name))
    ),
  ]
);

const emptyAttributeKeys: string[] = [];
export const selectDrofusEndringerAttributeKeys = (state: IRootState) =>
  state.drofusEndringer.attributeKeys ?? emptyAttributeKeys;

export const selectDrofusEndringerViewMode = createSelector(
  selectDrofusEndringerState,
  (state) => state.viewMode
);

export const selectDrofusEndringerSelectedOccurrenceRef = createSelector(
  selectDrofusEndringerState,
  (state) => state.selectedOccurrenceRef
);

const selectDrofusEndringerFilters = createSelector(
  (state: IRootState) => state.drofusEndringer.includeSynchronizationChanges,
  (state: IRootState) => state.drofusEndringer.search,
  (state: IRootState) => state.drofusEndringer.dateFrom,
  (state: IRootState) => state.drofusEndringer.dateTo,
  (state: IRootState) => state.drofusEndringer.changeTypes,
  (state: IRootState) => state.drofusEndringer.affectedDisciplines,
  (state: IRootState) => state.drofusEndringer.mmiCodes,
  (state: IRootState) => state.drofusEndringer.severities,
  (
    includeSynchronizationChanges,
    search,
    dateFrom,
    dateTo,
    changeTypes,
    affectedDisciplines,
    mmiCodes,
    severities
  ) => ({
    includeSynchronizationChanges,
    search,
    dateFrom,
    dateTo,
    changeTypes,
    affectedDisciplines,
    mmiCodes,
    severities,
  })
);

export const selectDrofusEndringerAttributeOptionsApiArg = createSelector(
  selectDrofusEndringerFilters,
  (state: IRootState) => state.design.projectNo,
  (state: IRootState) => state.pnsSelection.selectedIds,
  selectDrofusEndringerIsFloorView,
  selectDrofusEndringerFloorRoomNumbers,
  (
    drofusEndringer,
    projectNo,
    selectedPnsIds,
    isFloorView,
    roomFunctionNumbers
  ): IAttributeOptionsApiArg | null => {
    if (!Number.isFinite(projectNo) || projectNo <= 0) return null;
    if (isFloorView && roomFunctionNumbers.length === 0) return null;

    const search = drofusEndringer.search.trim();
    const pns = Array.from(new Set(selectedPnsIds.map((id) => id.trim()).filter(Boolean)));
    return {
      body: {
        projectNo,
        includeSynchronizationChanges: drofusEndringer.includeSynchronizationChanges ?? false,
        ...(search.length > 0 ? { search } : {}),
        ...(isFloorView ? { roomFunctionNumbers } : pns.length > 0 ? { pns } : {}),
        ...(drofusEndringer.dateFrom ? { dateFrom: drofusEndringer.dateFrom } : {}),
        ...(drofusEndringer.dateTo ? { dateTo: drofusEndringer.dateTo } : {}),
        ...(drofusEndringer.changeTypes.length > 0
          ? { changeTypes: drofusEndringer.changeTypes }
          : {}),
        ...(drofusEndringer.affectedDisciplines.length > 0
          ? { affectedDisciplines: drofusEndringer.affectedDisciplines }
          : {}),
        ...(drofusEndringer.mmiCodes.length > 0 ? { mmiCodes: drofusEndringer.mmiCodes } : {}),
        ...(drofusEndringer.severities.length > 0
          ? { severities: drofusEndringer.severities }
          : {}),
      },
    };
  }
);

export const selectDrofusEndringerRoomGroupsApiArg = createSelector(
  selectDrofusEndringerAttributeOptionsApiArg,
  selectDrofusEndringerAttributeKeys,
  (baseArg, attributeKeys): IRoomGroupsApiArg | null =>
    baseArg
      ? {
          body: { ...baseArg.body, ...(attributeKeys.length ? { attributeKeys } : {}) },
        }
      : null
);

export const selectDrofusEndringerApiArg = createSelector(
  selectDrofusEndringerRoomGroupsApiArg,
  selectDrofusEndringerState,
  (baseArg, state): IChangeEventsApiArg | null =>
    baseArg
      ? {
          body: { ...baseArg.body, limit: state.limit, offset: state.offset, sort: state.sort },
        }
      : null
);

const selectAttributeOptionsCacheSelector = createSelector(
  selectDrofusEndringerAttributeOptionsApiArg,
  (apiArg) =>
    apiArg
      ? openapi.endpoints.postMultiplacerDrofusInterfaceProjectChangeEventAttributeOptions.select(
          apiArg
        )
      : null
);
export const selectDrofusEndringerAttributeOptionsQueryResult = (state: IRootState) =>
  selectAttributeOptionsCacheSelector(state)?.(state);

const attributeCollator = new Intl.Collator("nb", { numeric: true });
export const selectDrofusEndringerAttributeOptions = createSelector(
  (state: IRootState) => selectDrofusEndringerAttributeOptionsQueryResult(state)?.data?.items,
  (items) =>
    (items ?? [])
      .map((item) => ({
        value: item.key,
        label: getDrofusChangeReason({
          changeKey: item.key,
          changeType: "updated",
          displayName: item.displayName?.trim() || undefined,
          affectedDisciplines: [],
        }).title.replace(/ endret$/, ""),
        count: item.count,
      }))
      .sort((a, b) => attributeCollator.compare(a.label, b.label) || a.value.localeCompare(b.value))
);

export const selectDrofusEndringerQueryResult = createSelector(
  [selectDrofusEndringerApiArg, (state: IRootState) => state],
  (apiArg, state) => {
    if (!apiArg) return undefined;
    return openapi.endpoints.postMultiplacerDrofusInterfaceProjectChangeEvents.select(apiArg)(
      state
    );
  }
);

export const selectDrofusEndringerRoomGroupsQueryResult = createSelector(
  [selectDrofusEndringerRoomGroupsApiArg, (state: IRootState) => state],
  (apiArg, state) => {
    if (!apiArg) return undefined;
    return openapi.endpoints.postMultiplacerDrofusInterfaceProjectChangeEventsByRoom.select(apiArg)(
      state
    );
  }
);

export const selectDrofusEndringerRows = createSelector(
  selectDrofusEndringerQueryResult,
  (queryResult) => queryResult?.data?.items ?? []
);

export const selectDrofusEndringerPagination = createSelector(
  selectDrofusEndringerState,
  selectDrofusEndringerQueryResult,
  (state, queryResult) => {
    const total = queryResult?.data?.total ?? 0;
    const limit = queryResult?.data?.limit ?? state.limit;
    const offset = queryResult?.data?.offset ?? state.offset;
    const rowCount = queryResult?.data?.items.length ?? 0;

    return {
      total,
      limit,
      offset,
      rowCount,
      totalPages: Math.max(1, Math.ceil(total / limit)),
      activePage: Math.floor(offset / limit) + 1,
      firstVisibleRow: total === 0 ? 0 : offset + 1,
      lastVisibleRow: Math.min(offset + rowCount, total),
    };
  }
);

export const selectDrofusEndringerRoomGroupsSummary = createSelector(
  selectDrofusEndringerRoomGroupsQueryResult,
  (queryResult) => {
    const total = queryResult?.data?.total ?? 0;
    const rowCount = queryResult?.data?.items.length ?? 0;

    return {
      total,
      rowCount,
      firstVisibleRow: total === 0 ? 0 : 1,
      lastVisibleRow: rowCount,
    };
  }
);

// Keep selected attributes removable when another filter removes them from the available options.
export const selectDrofusEndringerAttributeFilterOptions = createSelector(
  selectDrofusEndringerAttributeOptions,
  selectDrofusEndringerAttributeKeys,
  (options, selectedKeys) => {
    const available = new Set(options.map((option) => option.value));
    const missing = selectedKeys
      .filter((key) => !available.has(key))
      .map((key) => ({
        value: key,
        label: getDrofusChangeReason({
          changeKey: key,
          changeType: "updated",
          affectedDisciplines: [],
        }).title.replace(/ endret$/, ""),
        count: 0,
      }));
    return [...options, ...missing].sort(
      (a, b) => attributeCollator.compare(a.label, b.label) || a.value.localeCompare(b.value)
    );
  }
);

const selectDrofusEndringerSelectedRoomBaseApiArg = createSelector(
  selectDrofusEndringerRoomGroupsApiArg,
  (state: IRootState) => state.design.selectedSpaceNumber,
  (baseArg, roomFunctionNo): IChangeEventsApiArg | null =>
    baseArg &&
    roomFunctionNo &&
    (!baseArg.body.roomFunctionNumbers || baseArg.body.roomFunctionNumbers.includes(roomFunctionNo))
      ? { body: { ...baseArg.body, roomFunctionNo, limit: 100, offset: 0 } }
      : null
);
export const selectDrofusEndringerSelectedRoomSummaryApiArg = createSelector(
  selectDrofusEndringerSelectedRoomBaseApiArg,
  (arg): IRoomGroupsApiArg | null => {
    if (!arg) return null;
    const { roomFunctionNo, limit, offset, ...filters } = arg.body;
    return { body: { ...filters, roomFunctionNumbers: [roomFunctionNo!] } };
  }
);

export const selectDrofusEndringerRoomChangesPage = (state: IRootState) =>
  state.drofusEndringer.roomChangesPage ?? 1;
export const selectDrofusEndringerSelectedRoomApiArg = createSelector(
  selectDrofusEndringerSelectedRoomBaseApiArg,
  selectDrofusEndringerRoomChangesPage,
  (baseArg, page): IChangeEventsApiArg | null =>
    baseArg ? { body: { ...baseArg.body, offset: (page - 1) * 100 } } : null
);
